import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Phone, Mail, Edit, Trash2, UserPlus, MessageSquare, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const Atendimentos = () => {
  const [atendimentos, setAtendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAtendimento, setEditingAtendimento] = useState(null);
  const [showArquivados, setShowArquivados] = useState(false);
  const [showConvertidos, setShowConvertidos] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    email: '',
    assunto: '',
    observacoes: '',
    status: 'Pendente'
  });

  useEffect(() => {
    loadAtendimentos();
  }, []);

  const loadAtendimentos = async () => {
    try {
      const response = await api.getAtendimentos();
      setAtendimentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar atendimentos:', error);
      toast.error('Erro ao carregar atendimentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAtendimento) {
        await api.updateAtendimento(editingAtendimento.id, formData);
        toast.success('Atendimento atualizado com sucesso!');
      } else {
        await api.createAtendimento(formData);
        toast.success('Atendimento registrado com sucesso!');
      }
      handleCloseDialog();
      loadAtendimentos();
    } catch (error) {
      console.error('Erro ao salvar atendimento:', error);
      toast.error('Erro ao salvar atendimento');
    }
  };

  const handleEdit = (atendimento) => {
    setEditingAtendimento(atendimento);
    setFormData({
      nome: atendimento.nome,
      telefone: atendimento.telefone || '',
      cpf: atendimento.cpf || '',
      email: atendimento.email || '',
      assunto: atendimento.assunto,
      observacoes: atendimento.observacoes || '',
      status: atendimento.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (atendimentoId) => {
    if (!window.confirm('Deseja realmente excluir este atendimento?')) return;
    try {
      await api.deleteAtendimento(atendimentoId);
      toast.success('Atendimento excluído com sucesso!');
      loadAtendimentos();
    } catch (error) {
      console.error('Erro ao excluir atendimento:', error);
      toast.error('Erro ao excluir atendimento');
    }
  };

  const handleConverterCliente = async (atendimentoId) => {
    if (!window.confirm('Deseja converter este atendimento em cliente?')) return;
    try {
      await api.converterAtendimentoParaCliente(atendimentoId);
      toast.success('Atendimento convertido em cliente com sucesso!');
      loadAtendimentos();
    } catch (error) {
      console.error('Erro ao converter atendimento:', error);
      toast.error('Erro ao converter atendimento');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAtendimento(null);
    setFormData({
      nome: '',
      telefone: '',
      cpf: '',
      email: '',
      assunto: '',
      observacoes: '',
      status: 'Pendente'
    });
  };

  const limparFiltroData = () => {
    setDataInicio('');
    setDataFim('');
  };

  // Filtro por busca e data
  const filterAtendimentos = (list) => {
    return list.filter(atendimento => {
      const matchesSearch = atendimento.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        atendimento.assunto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        atendimento.cpf?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por data
      let matchesDate = true;
      if (dataInicio || dataFim) {
        const atendimentoDate = new Date(atendimento.created_at);
        atendimentoDate.setHours(0, 0, 0, 0);
        
        if (dataInicio) {
          const inicio = new Date(dataInicio);
          inicio.setHours(0, 0, 0, 0);
          if (atendimentoDate < inicio) matchesDate = false;
        }
        
        if (dataFim) {
          const fim = new Date(dataFim);
          fim.setHours(23, 59, 59, 999);
          if (atendimentoDate > fim) matchesDate = false;
        }
      }
      
      return matchesSearch && matchesDate;
    });
  };

  // Separar atendimentos por categoria
  const atendimentosAtivos = filterAtendimentos(
    atendimentos.filter(a => a.status === 'Pendente' || a.status === 'Em análise')
  );
  
  const atendimentosConvertidos = filterAtendimentos(
    atendimentos.filter(a => a.status === 'Convertido')
  );
  
  const atendimentosArquivados = filterAtendimentos(
    atendimentos.filter(a => a.status === 'Arquivado')
  );

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'bg-amber-100 text-amber-700',
      'Em análise': 'bg-blue-100 text-blue-700',
      'Convertido': 'bg-green-100 text-green-700',
      'Arquivado': 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const renderAtendimentoCard = (atendimento) => (
    <Card key={atendimento.id} className="hover:shadow-lg transition-shadow" data-testid="atendimento-card">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-slate-600" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg truncate">{atendimento.nome}</CardTitle>
              <span className={`inline-block mt-1 text-xs px-2 py-1 rounded ${getStatusColor(atendimento.status)}`}>
                {atendimento.status}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-4 sm:p-6 pt-0">
        <p className="text-sm text-slate-700 font-medium">{atendimento.assunto}</p>
        {atendimento.telefone && (
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{atendimento.telefone}</span>
          </div>
        )}
        {atendimento.email && (
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{atendimento.email}</span>
          </div>
        )}
        {atendimento.cpf && (
          <p className="text-xs sm:text-sm text-slate-600">CPF: {atendimento.cpf}</p>
        )}
        <p className="text-xs text-slate-500 mt-2">
          Registrado em: {new Date(atendimento.created_at).toLocaleDateString('pt-BR')}
        </p>
        <div className="flex gap-2 mt-3 pt-3 border-t">
          {atendimento.status !== 'Convertido' && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs sm:text-sm"
              onClick={() => handleConverterCliente(atendimento.id)}
              data-testid="converter-cliente-button"
            >
              <UserPlus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Converter</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit(atendimento)}
            data-testid="edit-atendimento-button"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDelete(atendimento.id)}
            data-testid="delete-atendimento-button"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="p-4 sm:p-8" data-testid="atendimentos-loading">Carregando...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="atendimentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Atendimentos</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Registre contatos iniciais e potenciais clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-atendimento-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Atendimento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAtendimento ? 'Editar Atendimento' : 'Registrar Novo Atendimento'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  data-testid="atendimento-nome-input"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    data-testid="atendimento-telefone-input"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    data-testid="atendimento-cpf-input"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="atendimento-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="assunto">Assunto *</Label>
                <Input
                  id="assunto"
                  data-testid="atendimento-assunto-input"
                  required
                  placeholder="Ex: Consulta sobre divórcio, Processo trabalhista..."
                  value={formData.assunto}
                  onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="atendimento-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pendente">Pendente</SelectItem>
                    <SelectItem value="Em análise">Em análise</SelectItem>
                    <SelectItem value="Convertido">Convertido</SelectItem>
                    <SelectItem value="Arquivado">Arquivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <textarea
                  id="observacoes"
                  className="w-full min-h-[80px] p-2 border rounded-md"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-atendimento-button">
                  {editingAtendimento ? 'Atualizar' : 'Registrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros */}
      <div className="space-y-4">
        {/* Busca */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar por nome, assunto ou CPF..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-atendimentos-input"
          />
        </div>
        
        {/* Filtro por data */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4" />
            <span>Filtrar por data:</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="dataInicio" className="text-sm whitespace-nowrap">De:</Label>
              <Input
                id="dataInicio"
                type="date"
                className="w-full sm:w-40"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                data-testid="filter-data-inicio"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="dataFim" className="text-sm whitespace-nowrap">Até:</Label>
              <Input
                id="dataFim"
                type="date"
                className="w-full sm:w-40"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                data-testid="filter-data-fim"
              />
            </div>
            {(dataInicio || dataFim) && (
              <Button variant="outline" size="sm" onClick={limparFiltroData}>
                Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Atendimentos Ativos (Pendente e Em análise) */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-3 h-3 bg-amber-400 rounded-full"></span>
          Atendimentos Ativos ({atendimentosAtivos.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {atendimentosAtivos.length > 0 ? (
            atendimentosAtivos.map(renderAtendimentoCard)
          ) : (
            <div className="col-span-full text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
              Nenhum atendimento pendente ou em análise
            </div>
          )}
        </div>
      </div>

      {/* Atendimentos Convertidos (Colapsável) */}
      {atendimentosConvertidos.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <button
            className="w-full p-4 bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-between"
            onClick={() => setShowConvertidos(!showConvertidos)}
            data-testid="toggle-convertidos"
          >
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="font-semibold text-green-800">
                Convertidos em Cliente ({atendimentosConvertidos.length})
              </span>
            </div>
            {showConvertidos ? (
              <ChevronUp className="w-5 h-5 text-green-700" />
            ) : (
              <ChevronDown className="w-5 h-5 text-green-700" />
            )}
          </button>
          {showConvertidos && (
            <div className="p-4 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {atendimentosConvertidos.map(renderAtendimentoCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Atendimentos Arquivados (Colapsável) */}
      {atendimentosArquivados.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <button
            className="w-full p-4 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between"
            onClick={() => setShowArquivados(!showArquivados)}
            data-testid="toggle-arquivados"
          >
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-slate-400 rounded-full"></span>
              <span className="font-semibold text-slate-700">
                Arquivados ({atendimentosArquivados.length})
              </span>
            </div>
            {showArquivados ? (
              <ChevronUp className="w-5 h-5 text-slate-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-600" />
            )}
          </button>
          {showArquivados && (
            <div className="p-4 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {atendimentosArquivados.map(renderAtendimentoCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Atendimentos;
